import { MultiAutocompleteInput, type SelectOption } from '@components';
import { Field, type FieldAttributes, useFormikContext } from 'formik';
import { type ReactElement, type ReactNode, useEffect, useRef, useState } from 'react';

import { LoadingOption } from './utils';

type MultiLookupInputProps<FormikFieldType, OptionValueType> = {
  label?: string;
  name: string;
  options: SelectOption<OptionValueType>[];

  /** Called with the selected options, or `[]`, before the field is touched. Doesn't set the
   * field: the callback must write the Formik value itself, and still sees the previous one. */
  onMatchChange: (matches: SelectOption<OptionValueType>[]) => void;

  /** Finds the options that correspond to what's in Formik state. */
  formValueMatcher: (
    formValue: FormikFieldType[],
    options: SelectOption<OptionValueType>[],
  ) => SelectOption<OptionValueType>[];

  /** @default empty */
  placeholder?: string;

  /** @default undefined */
  disabled?: boolean;
  /** Adding a tip will wrap the input component in a hoverable tooltip */
  tip?: string;
  /** if true - the options will be displayed with checkbox */
  withCheckbox?: boolean;

  /** custom render function for the value of the input. */
  renderValue?: (selected: SelectOption<OptionValueType>[]) => ReactNode;

  /**
   * Controlled text value shown in the autocomplete input.
   * Use with `onInputChange` for a fully controlled typing experience.
   */
  inputValue?: string;
  /**
   * Callback fired whenever the user types in the input field.
   * Combine with `inputValue` to control the text and trigger server-side searches.
   */
  onInputValueChange?: (value: string) => void;

  /**
   * Use if options are filtered somewhere else, like during API requests.
   * Combine with onInputValueChange and inputValue to get the filter string.
   */
  disableClientSideFiltering?: boolean;
  /** When true, the helper text area is not rendered when there is no error. Default false. */
  omitEmptyHelperText?: boolean;
};

/** A Slabstack formik-friendly multi-selection autocomplete.
 * Must be used inside `<Formik><Form>{component}</Form></Formik>`. */
export const MultiLookupInput = <FormikFieldType, OptionValueType>({
  label,
  name,
  options,
  onMatchChange,
  formValueMatcher,
  placeholder,
  disabled,
  tip,
  withCheckbox,
  renderValue,
  onInputValueChange,
  inputValue,
  disableClientSideFiltering,
  omitEmptyHelperText = false,
}: MultiLookupInputProps<FormikFieldType, OptionValueType>): ReactElement => {
  const { getFieldMeta } = useFormikContext();
  const formikValue = getFieldMeta(name).value as FormikFieldType[];

  // Cache of previously selected options so they survive server-side filtering.
  // Keyed by stringified option value for O(1) lookups.
  const selectedCacheRef = useRef<Map<string, SelectOption<OptionValueType>>>(new Map());

  const resolveStateOpts = (): SelectOption<OptionValueType>[] => {
    // sorted by name
    const cachedValues = [...selectedCacheRef.current.values()];
    const allKnownOptions = [
      ...cachedValues.filter(
        (cached) => !options.some((opt) => String(opt.value) === String(cached.value)),
      ),
      ...options,
    ].sort((a, b) => a.label.localeCompare(b.label));

    return formValueMatcher(formikValue, allKnownOptions);
  };

  const updateCache = (resolved: SelectOption<OptionValueType>[]): void => {
    const resolvedKeys = new Set(resolved.map((r) => String(r.value)));
    for (const key of selectedCacheRef.current.keys()) {
      if (!resolvedKeys.has(key)) {
        selectedCacheRef.current.delete(key);
      }
    }
    resolved.forEach((opt) => {
      selectedCacheRef.current.set(String(opt.value), opt);
    });
  };

  const [stateOpts, setStateOpts] = useState<SelectOption<OptionValueType>[]>(() => {
    const initial = resolveStateOpts();
    updateCache(initial);
    return initial;
  });

  useEffect(() => {
    const resolved = resolveStateOpts();
    updateCache(resolved);
    setStateOpts(resolved);
  }, [formikValue, options]);

  return (
    <Field id={name} name={name}>
      {({
        field: { onBlur },
        form: { setFieldTouched },
        meta,
      }: FieldAttributes<any>): ReactElement => {
        const loadingOptions = options.length === 1 && options[0] === LoadingOption;
        const hasError = meta.touched === true && meta.error !== undefined;
        let helperText = omitEmptyHelperText ? undefined : ' ';
        if (hasError) {
          helperText = meta.error;
        }
        return (
          <MultiAutocompleteInput
            onBlur={onBlur}
            disabled={disabled}
            loading={loadingOptions}
            error={hasError}
            helperText={helperText}
            placeholder={placeholder}
            tip={tip}
            label={label}
            name={name}
            options={options}
            value={stateOpts}
            inputValue={inputValue}
            onInputValueChange={onInputValueChange}
            disableClientSideFiltering={disableClientSideFiltering}
            onChange={(selectedOpts: SelectOption<OptionValueType>[]): void => {
              updateCache(selectedOpts);
              onMatchChange(selectedOpts);
              setStateOpts(selectedOpts);
              requestAnimationFrame(() => {
                setFieldTouched(name, true);
              });
            }}
            withCheckbox={withCheckbox}
            renderValue={renderValue}
          />
        );
      }}
    </Field>
  );
};
